import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { AssessmentState } from './state';
import SentryClient from '@/helpers/sentry';
import {
  UserActivitiesDetails,
  UserGradesService,
} from '@/services/undergrad/pegasus-pepe-legal/user-grades.service';
import { ExercisesEvaluationService } from '@/services/undergrad/pegasus-pepe-legal/exercises-evaluation.service';

interface PullEvaluationAndDeadlines {
  disciplineInstanceIds: string[];
  studyWindowId: string;
}

const actions: ActionTree<AssessmentState, RootState> = {
  async PULL_DISCIPLINE_GRADES(
    _,
    payload: { disciplineId: string },
  ): Promise<UserActivitiesDetails> {
    try {
      const { disciplineId } = payload;

      return await new UserGradesService().getDisciplineGrades(disciplineId);
    } catch (error) {
      SentryClient.captureError(error);
      throw error;
    }
  },

  async PULL_PERSISTED_DISCIPLINE_GRADES(
    _,
    payload: { disciplineId: string; isDisciplineMigrated: boolean; disciplineType?: string },
  ): Promise<UserActivitiesDetails> {
    try {
      const { disciplineId, isDisciplineMigrated, disciplineType } = payload;

      return await new UserGradesService().getPersistedDisciplineGrade(
        disciplineId,
        isDisciplineMigrated,
        disciplineType,
      );
    } catch (error) {
      SentryClient.captureError(error);
      throw error;
    }
  },

  async PULL_ACTIVE_STUDY_WINDOWS({ state, commit }): Promise<void> {
    try {
      if (state.activeStudyWindows === undefined) {
        const studyWindows = await new UserGradesService().getActiveStudyWindows();

        if (studyWindows) {
          commit('SET_ACTIVE_STUDY_WINDOWS', studyWindows);
        }
      }
    } catch (error) {
      SentryClient.captureError(error);
    }
  },

  async PULL_EVALUATION_AND_DEADLINES(
    { commit, state },
    payload: PullEvaluationAndDeadlines,
  ): Promise<void> {
    try {
      const { disciplineInstanceIds, studyWindowId } = payload as PullEvaluationAndDeadlines;

      const filteredDisciplineInstanceIds = disciplineInstanceIds.filter((id) => {
        if (!state.evaluationAndDeadlines) return true;
        const alreadyOnState = state.evaluationAndDeadlines.find(
          (evaluation) => evaluation.disciplineInstanceId === id,
        );

        return !alreadyOnState;
      });

      if (filteredDisciplineInstanceIds.length === 0) return;

      const evaluationAndDeadlines =
        await new ExercisesEvaluationService().getEvaluationAndDeadlines({
          disciplineInstanceIds: filteredDisciplineInstanceIds,
          studyWindowId,
        });

      if (evaluationAndDeadlines) {
        commit('SET_EVALUATION_AND_DEADLINES', evaluationAndDeadlines);
      }
    } catch (error) {
      SentryClient.captureError(error);
    }
  },
};

export default actions;
